import { useNavigate } from "react-router-dom";
import { Zap, ArrowRight } from "lucide-react";

function CountdownPill({ value, label }) {
  return (
    <div style={{ textAlign: "center", minWidth: 52 }}>
      <div style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: "6px 12px", backdropFilter: "blur(8px)" }}>
        <span style={{ fontSize: 28, fontWeight: 900, color: "#FACC15", fontFamily: "Outfit, sans-serif", lineHeight: 1 }}>
          {String(value).padStart(2, "0")}
        </span>
      </div>
      <span style={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.45)", letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 3, display: "block" }}>
        {label}
      </span>
    </div>
  );
}

/**
 * Reusable election spotlight banner used for SA 2026, VIC 2026, and NSW 2027.
 */
export function ElectionSpotlightBanner({
  countdown,
  testId,
  badge,
  bgGradient,
  borderColor,
  glowColor,
  accentColor,
  topBarGradient,
  title,
  subtitle,
  issues,
  guidePath,
  guideLabel,
  countdownNumColor,
  isPrimary,
  extraBadges,
}) {
  const navigate = useNavigate();
  if (!countdown) return null;

  return (
    <section className="px-4 pb-6" data-testid={testId}>
      <div className="max-w-6xl mx-auto">
        <div
          className="overflow-hidden relative"
          style={{
            background: bgGradient,
            border: `1px solid ${borderColor}`,
            borderRadius: 20,
            boxShadow: `0 24px 60px -12px ${glowColor}, 0 4px 20px rgba(0,0,0,0.5)`,
          }}
        >
          {glowColor && (
            <div style={{
              position: "absolute", top: 0, left: "30%",
              width: 400, height: 300,
              background: `radial-gradient(ellipse at center, ${glowColor} 0%, transparent 70%)`,
              filter: "blur(40px)", pointerEvents: "none",
            }} />
          )}
          <div style={{ height: 3, background: topBarGradient }} />
          <div className="p-6 sm:p-8 relative">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {isPrimary && (
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full" style={{ background: `${accentColor}20`, border: `1px solid ${accentColor}40` }}>
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: accentColor }} />
                  <span className="text-xs font-bold tracking-wide" style={{ color: accentColor }}>NEXT ELECTION</span>
                </div>
              )}
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.10)" }}>
                {badge}
              </span>
              {extraBadges?.map((b) => (
                <span key={b} className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: "rgba(234,179,8,0.10)", color: "#FACC15", border: "1px solid rgba(234,179,8,0.20)" }}>
                  {b}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="flex-1">
                <h2 className="text-2xl sm:text-3xl font-black text-white mb-2 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {title}
                </h2>
                <p className="text-sm mb-5" style={{ color: "#94A3B8" }}>
                  {countdown.over ? `${badge} has voted! See the full election guide.` : subtitle}
                </p>

                {!countdown.over ? (
                  isPrimary ? (
                    <div className="flex items-end gap-4 flex-wrap">
                      <div className="flex items-end gap-3">
                        <CountdownPill value={countdown.days} label="days" />
                        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 22, fontWeight: 900, marginBottom: 18 }}>:</span>
                        <CountdownPill value={countdown.hours} label="hrs" />
                        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 22, fontWeight: 900, marginBottom: 18 }}>:</span>
                        <CountdownPill value={countdown.minutes} label="min" />
                      </div>
                      <span className="text-xs font-medium mb-5" style={{ color: "#64748B" }}>until polls open</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 flex-wrap">
                      {[{ value: countdown.days, label: "days" }, { value: countdown.hours, label: "hrs" }, { value: countdown.minutes, label: "min" }].map(({ value, label }) => (
                        <div key={label} className="flex items-baseline gap-1">
                          <span className="text-2xl font-black" style={{ color: countdownNumColor || "#94A3B8", fontFamily: "Outfit, sans-serif" }}>{String(value).padStart(2, "0")}</span>
                          <span className="text-xs font-medium" style={{ color: "#475569" }}>{label}</span>
                        </div>
                      ))}
                      <span className="text-xs font-medium" style={{ color: "#475569" }}>until polls open</span>
                    </div>
                  )
                ) : (
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: `${accentColor}20`, border: `1px solid ${accentColor}40` }}>
                    <Zap style={{ width: 14, height: 14, color: accentColor }} />
                    <span className="text-sm font-semibold" style={{ color: accentColor }}>{badge} has voted — Explore results &amp; profiles</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 sm:items-end flex-shrink-0">
                {issues.map((issue) => (
                  <div key={issue} className="flex items-center gap-1.5 text-xs" style={{ color: "#94A3B8" }}>
                    <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: accentColor }} />
                    {issue}
                  </div>
                ))}
                <button
                  onClick={() => navigate(guidePath)}
                  className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:-translate-y-0.5 active:scale-[0.98]"
                  style={{ background: isPrimary ? `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)` : `${accentColor}25`, color: isPrimary ? "#fff" : countdownNumColor || "#F8FAFC", border: isPrimary ? "none" : `1px solid ${accentColor}40`, boxShadow: isPrimary ? `0 6px 18px ${accentColor}50` : "none" }}
                  data-testid={`view-${testId}-btn`}
                >
                  {guideLabel} <ArrowRight style={{ width: 15, height: 15 }} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
